/**
 * Recommendation cards — API client
 */

import { api } from '../../services/api';

export interface CardSummary {
  reference: string;
  agentKey: string;
  problem: string;
  recommendation: string;
  annualValueEgp: number;
  impactSummary: string;
  confidence: 'high' | 'medium' | 'low';
  category: string;
  approverChain: string[];
  controlMetric: string;
  windowDays: number;
  submittedAt: string | null;
  status: string;
}

export interface ImpactInput {
  label: string;
  value: number;
  unit: string;
  /** `measured` from this client, `market` from an external signal. */
  kind: 'measured' | 'market';
  source: string;
}

export interface SizedImpact {
  model: string;
  annualValueEgp: number;
  summary: string;
  inputs: ImpactInput[];
  /** The arithmetic, in order, so it can be checked by hand. */
  workings: string[];
  confidence: 'high' | 'medium' | 'low';
  caveats: string[];
}

export interface CardDetail {
  reference: string;
  agentKey: string;
  problem: string;
  rootCause: string;
  recommendation: string;
  impact: SizedImpact;
  confidence: {
    level: 'high' | 'medium' | 'low';
    monthsOfData: number;
    recordsExamined: number;
    caveats: string[];
  };
  effort: {
    description: string;
    personDays: number;
    oneOffCostEgp: number;
    requiredFrom: string[];
  };
  risksAndControls: string[];
  peopleImpact: string;
  category: string;
  approverChain: string[];
  verification: {
    controlMetricKey: string;
    baselineValue: number;
    targetValue: number;
    windowDays: number;
    rollbackTrigger: string;
  };
  evidence: Array<{
    citationId: string;
    kind: 'measured' | 'knowledge' | 'signal' | 'tool';
    statement: string;
    source: string;
  }>;
  annotations: Array<{
    author: string;
    authorRole: string;
    kind: 'confirm' | 'context' | 'correct' | 'dispute';
    note: string;
    createdAt: string;
  }>;
  status: string;
  referenceDate: string;
}

export function formatEgp(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

class CardsService {
  /** The Decision Inbox: pending cards, most valuable first. */
  async listPending(): Promise<CardSummary[]> {
    const response = await api.get<Envelope<CardSummary[]>>('/core/cards');
    return response.data ?? [];
  }

  async getCard(reference: string): Promise<CardDetail> {
    const response = await api.get<Envelope<CardDetail>>(`/core/cards/${reference}`);
    return response.data;
  }

  /**
   * Record a decision. A rejection requires a note — the server refuses one
   * without it, because the reason is the only part of a rejection with any
   * future value.
   */
  async decide(
    reference: string,
    decision: 'approved' | 'approved_with_changes' | 'piloting' | 'rejected',
    note?: string
  ): Promise<void> {
    await api.post(`/core/cards/${reference}/decide`, { decision, note });
  }

  /** Append an expert annotation. Never an edit. */
  async annotate(
    reference: string,
    kind: 'confirm' | 'context' | 'correct' | 'dispute',
    note: string
  ): Promise<void> {
    await api.post(`/core/cards/${reference}/annotate`, { kind, note });
  }
}

export const cardsService = new CardsService();
